import ButtonSize from '@/Enums/ButtonSize.enum';
import { geoTypeLabel } from '@/Enums/Geography/GeographyType.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import { CapturePDFButton } from '@/components/Buttons/CapturePDFButton';
import { DownloadButton } from '@/components/Buttons/DownloadButton';
import RadioSwitch from '@/components/RadioSwitch';
import IOSSwitch from '@/components/Switch';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { GREY } from '@/constants/color';
import { useAppSelector } from '@/store/hooks';
import { InfoOutlined } from '@mui/icons-material';
import { RadioGroup } from '@mui/material';
import { GridToolbarContainer, gridFilteredSortedRowIdsSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import GeoChip from './GeoChip';
import ImpressionFilterRadioButton from './ImpressionFilterRadioButton';

const getSelectedRowsToExport = ({ apiRef }) => {
  return gridFilteredSortedRowIdsSelector(apiRef);
};

const ToolBar = ({
  printPdfMode,
  showViewSwitch,
  viewTypes,
  viewType,
  onViewTypeChange,
  currentType,
  currentTypeLabel,
  onTypeChange,
  impressionTypes,
  handleRadioToggle,
  mapScales,
  checkedForPrintList,
  handlePrintPdfModeToggle,
  handlePrintTable,
  disabled = false,
}) => {
  const apiRef = useGridApiContext();
  const { apiTab } = useAppSelector(state => state.reportingDashboard);
  const typeLabel = geoTypeLabel(currentTypeLabel);

  const onSaveClick = () => {
    apiRef.current.exportDataAsCsv({
      getRowsToExport: getSelectedRowsToExport,
      fileName: `Reporting_Dashboard_impressions_by_${typeLabel?.replace(' ', '_')}_${Date.now()}`,
    });
  };

  const handleTypeChange = (_, value) => {
    apiRef.current.setPage(0);
    onTypeChange(value);
  };

  return (
    <GridToolbarContainer
      sx={{
        padding: 0,
        '@media print': {
          display: printPdfMode && 'none',
        },
      }}
    >
      <div className='flex justify-between mb-[25px] print:mb-[10px] w-full'>
        <div id='impressions-by-type-label'>
          <div className='flex items-center gap-2'>
            <div className='text-lg text-[#2E2C34] font-semibold hidden print:block'>Impressions by {typeLabel}</div>
            <div className='text-lg text-[#2E2C34] font-semibold print:hidden'>Impressions by Type</div>
            <InfoOutlined sx={{ width: '16px', height: '16px', color: GREY, displayPrint: 'none' }} />
          </div>
          <span className='text-[12px] text-[#84818A] print:hidden'>
            Please click the graph symbol next to each keyword to display it on the graph.
          </span>
        </div>
        <div className='flex gap-6 justify-between h-[36px] print:hidden'>
          <div className='flex items-center gap-3'>
            <IOSSwitch checked={printPdfMode} onChange={handlePrintPdfModeToggle} disabled={disabled} />
            <p className={`font-semibold text-[14px] ${printPdfMode ? 'text-[#303030]' : 'text-[#84818A]'}`}>
              Print PDF Mode
            </p>
          </div>
          {showViewSwitch && (
            <RadioSwitch value={viewType} onChange={onViewTypeChange} options={viewTypes} disabled={disabled} />
          )}
          <div className='flex flex-row space-x-[9px]'>
            <DownloadButton onClick={onSaveClick} size={ButtonSize.Small} disabled={disabled} />
            <CapturePDFButton onClick={handlePrintTable} size={ButtonSize.Small} disabled={disabled} />
          </div>
        </div>
      </div>
      <RadioGroup
        row
        value={currentType}
        onChange={handleTypeChange}
        aria-labelledby='impressions-by-type-label'
        name='impressionTypes'
        sx={{
          displayPrint: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '8px',
          marginBottom: '25px',
          width: '100%',
        }}
      >
        {impressionTypes?.map(type => (
          <Fragment key={type.label}>
            <ImpressionFilterRadioButton
              label={type.label}
              value={type.value}
              printPdfMode={printPdfMode}
              checkedForPrint={checkedForPrintList.some(item => item === type.value)}
              onChange={handleRadioToggle}
              disabled={disabled}
            >
              {type.label}
            </ImpressionFilterRadioButton>
            {apiTab === ApiTabIndex.Google && type.value === ReportView.Publisher && (
              <div className='relative w-2'>
                <InfoTooltip
                  className='right-0 top-[-17px]'
                  title='Publisher tab does not work in conjunction with Advertiser filter above. Please use Campaign filter instead.'
                />
              </div>
            )}
          </Fragment>
        ))}
        {mapScales?.length ? (
          printPdfMode ? (
            mapScales?.map(mapScale => (
              <ImpressionFilterRadioButton
                key={mapScale}
                label={geoTypeLabel(mapScale)}
                value={mapScale}
                printPdfMode={printPdfMode}
                checkedForPrint={checkedForPrintList.some(item => item === mapScale)}
                onChange={handleRadioToggle}
                disabled={disabled}
              >
                {geoTypeLabel(mapScale)}
              </ImpressionFilterRadioButton>
            ))
          ) : (
            <GeoChip value={currentType} onChange={onTypeChange} mapScales={mapScales} disabled={disabled} />
          )
        ) : null}
      </RadioGroup>
    </GridToolbarContainer>
  );
};

ToolBar.propTypes = {
  printPdfMode: PropTypes.bool,
  showViewSwitch: PropTypes.bool,
  viewTypes: PropTypes.array,
  viewType: PropTypes.string,
  onViewTypeChange: PropTypes.func,
  currentType: PropTypes.string,
  currentTypeLabel: PropTypes.string,
  onTypeChange: PropTypes.func,
  impressionTypes: PropTypes.array,
  handleRadioToggle: PropTypes.func,
  mapScales: PropTypes.array,
  checkedForPrintList: PropTypes.array,
  handlePrintPdfModeToggle: PropTypes.func,
  handlePrintTable: PropTypes.func,
  disabled: PropTypes.bool,
};

export default ToolBar;
